import type { FC } from "react";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import { Navigate, Route, Routes, useParams } from "react-router";
import type { TemplateFormQuestion, TemplateFormSettings } from "../../../api/templateFormSettings.types";
import { usePrivacyNavigate } from "../../../shared/usePrivacyNavigate";
import { TemplateDialogMenu } from "./TemplateFormDialogMenu";
import { TemplateFormSectionsGrid } from "./TemplateFormSectionsGrid";

export const TemplateFormDialogContent: FC<{ formSettings: TemplateFormSettings }> = ({ formSettings }) => {
  const params = useParams<{ sectionId: string | undefined }>();
  if (!params.sectionId) throw new Error("params.sectionId is required");

  const { navigate } = usePrivacyNavigate();
  const currentSection = formSettings.sections.find(section => params.sectionId === section.id);

  if (!currentSection) return <Navigate to={`../${formSettings.sections[0].id}`} replace />;

  const questionRows = questionsToQuestionRows(currentSection.questions);

  return (
    <Stack direction="row">
      <TemplateDialogMenu
        formSettings={formSettings}
        selectedSectionId={currentSection.id}
        onSectionSelect={sectionId => navigate(`../${sectionId}`)}
      />
      <Box sx={{ minWidth: 0, flex: 1, bgcolor: theme => theme.palette.background.default }}>
        <Routes>
          <Route
            path=":questionId"
            element={<TemplateFormSectionsGrid formId={formSettings.id} questionRows={questionRows} />}
          />
          <Route index element={<TemplateFormSectionsGrid formId={formSettings.id} questionRows={questionRows} />} />
        </Routes>
      </Box>
    </Stack>
  );
};

export type TemplateFormQuestionRow = TemplateFormQuestion & {
  parentId?: string | null;
  path: string[];
};

const questionsToQuestionRows = (
  items: TemplateFormQuestion[],
  path: string[] = [],
  parentId: string | null = null
): TemplateFormQuestionRow[] =>
  items.flatMap(item => [
    { ...item, parentId, path: [...path, item.header] },
    ...questionsToQuestionRows(item.followUps ?? [], [...path, item.header], item.id),
  ]);
